import { z } from 'zod';

/**
 * Domänentypen (Fachmodell siehe plan/Stufe-02_Domaene-Buchungslogik.md).
 * IDs von Schulungen, Modulen, Terminen und Buchungen sind SharePoint-Item-IDs (Zahlen).
 * Personen werden über ihre Anmeldekennung (UPN, Kleinbuchstaben) identifiziert.
 * Zeitpunkte sind ISO-8601-Strings mit Zeitzone.
 * Personendaten nur laut S-07: Name, Kennung, Lernpfad, Copilot, Buchungen.
 */

const isoZeitpunkt = z.iso.datetime({ offset: true });
const kennung = z
  .string()
  .min(3)
  .regex(/^[^\s@]+@[^\s@]+$/, 'Ungültige Anmeldekennung')
  .transform((s) => s.toLowerCase());

export const RolleSchema = z.enum(['Admin']);
export type Rolle = z.infer<typeof RolleSchema>;

/** Lernpfad = Pflichtprogramm einer Gruppe (Office/Power), Zusatz = Copilot. */
export const SchulungArtSchema = z.enum(['lernpfad', 'zusatz']);
export type SchulungArt = z.infer<typeof SchulungArtSchema>;

/** Schlüssel der Zusatzschulung, die an der Copilot-Berechtigung hängt. */
export const COPILOT_SCHLUESSEL = 'copilot';

export const SchulungSchema = z.object({
  id: z.number().int().positive(),
  /** Stabiler fachlicher Schlüssel, z. B. `office`, `power`, `copilot`. */
  schluessel: z.string().min(1),
  art: SchulungArtSchema,
  /** Neutrale, konfigurierbare Bezeichnung in der Oberfläche (S-09), z. B. „Lernpfad A“. */
  anzeigename: z.string().min(1),
  sortierung: z.number().int(),
  /**
   * Plätze je Termin dieser Schulung, zentral in den Einstellungen gepflegt. Gilt für alle Termine, auch für
   * bereits angelegte; nur ein Termin mit `eigeneObergrenze` weicht davon ab.
   */
  standardObergrenze: z.number().int().positive(),
  aktiv: z.boolean(),
});
export type Schulung = z.infer<typeof SchulungSchema>;

export const ModulSchema = z.object({
  id: z.number().int().positive(),
  schulungId: z.number().int().positive(),
  /** Schlüssel in der Terminliste (Import), z. B. `office1`, `officecoaching`, `copilot`. */
  schluessel: z.string().min(1),
  titel: z.string().min(1),
  reihenfolge: z.number().int().positive(),
  beschreibung: z.string().optional(),
  dauerMinuten: z.number().int().positive(),
  istCoaching: z.boolean(),
  aktiv: z.boolean(),
});
export type Modul = z.infer<typeof ModulSchema>;

export const TerminFormatSchema = z.enum(['praesenz', 'online']);
export type TerminFormat = z.infer<typeof TerminFormatSchema>;

export const TerminStatusSchema = z.enum(['geplant', 'abgesagt']);
export type TerminStatus = z.infer<typeof TerminStatusSchema>;

export const TerminSchema = z.object({
  id: z.number().int().positive(),
  modulId: z.number().int().positive(),
  schulungId: z.number().int().positive(),
  beginn: isoZeitpunkt,
  ende: isoZeitpunkt,
  format: TerminFormatSchema,
  ort: z.string().optional(),
  teamsLink: z.url().optional(),
  /**
   * Online-Termine mit Teams-Termin im Kampagnen-Postfach: ID dieses einen Kalendertermins, in den alle Gebuchten
   * eingetragen werden. Nur vom System gesetzt; fehlt sie, laden die Buchungen einzeln ein.
   */
  kalenderEventId: z.string().optional(),
  /** Ausnahme: eigene Platzzahl nur für diesen Termin. Fehlt sie, gilt die Einstellung der Schulung. */
  eigeneObergrenze: z.number().int().positive().optional(),
  /**
   * Wirksame Platzzahl (`wirksameObergrenze`): `eigeneObergrenze`, sonst `Schulung.standardObergrenze`. Das
   * Repository leitet sie beim Lesen ab; gespeichert wird nur `eigeneObergrenze`.
   */
  obergrenze: z.number().int().positive(),
  /** Nur für die Anzeige; maßgeblich sind die aktiven Buchungen (nächtlicher Abgleich). */
  anzahlGebucht: z.number().int().nonnegative(),
  /** Trainer als Freitext – Trainer haben keinen Zugang zur Anwendung. */
  trainerText: z.string().optional(),
  status: TerminStatusSchema,
  hinweise: z.string().optional(),
});
export type Termin = z.infer<typeof TerminSchema>;

export const TeilnehmerSchema = z.object({
  id: z.number().int().positive(),
  kennung,
  name: z.string().min(1),
  /** Schlüssel der Lernpfad-Schulung (z. B. `office` oder `power`). */
  lernpfad: z.string().min(1),
  copilot: z.boolean(),
  aktiv: z.boolean(),
  importiertAm: isoZeitpunkt,
});
export type Teilnehmer = z.infer<typeof TeilnehmerSchema>;

export const BuchungStatusSchema = z.enum(['gebucht', 'storniert']);
export type BuchungStatus = z.infer<typeof BuchungStatusSchema>;

export const StornoGrundSchema = z.enum([
  'Teilnehmer',
  'Kaskade',
  'Umbuchung',
  'TerminAbgesagt',
  'KalenderAbgelehnt',
  'Import',
  'Admin',
]);
export type StornoGrund = z.infer<typeof StornoGrundSchema>;

export const KalenderStatusSchema = z.enum(['ausstehend', 'versendet', 'fehler', 'abgesagt']);
export type KalenderStatus = z.infer<typeof KalenderStatusSchema>;

/** Nur der Status der Antwort – Antworttexte werden nie gelesen oder gespeichert (S-06). */
export const KalenderAntwortSchema = z.enum(['keine', 'zugesagt', 'vorlaeufig', 'abgelehnt']);
export type KalenderAntwort = z.infer<typeof KalenderAntwortSchema>;

export const BuchungSchema = z.object({
  id: z.number().int().positive(),
  terminId: z.number().int().positive(),
  modulId: z.number().int().positive(),
  schulungId: z.number().int().positive(),
  kennung,
  name: z.string().min(1),
  status: BuchungStatusSchema,
  gebuchtAm: isoZeitpunkt,
  /** Kennung dessen, der gebucht hat (Teilnehmer selbst oder Admin im Auftrag, F-24). */
  gebuchtVon: z.string().min(1),
  storniertAm: isoZeitpunkt.optional(),
  storniertVon: z.string().optional(),
  stornoGrund: StornoGrundSchema.optional(),
  kalenderEventId: z.string().optional(),
  kalenderStatus: KalenderStatusSchema,
  kalenderAntwort: KalenderAntwortSchema,
});
export type Buchung = z.infer<typeof BuchungSchema>;

export const EinstellungenSchema = z.object({
  /** Buchungsschluss in Stunden vor Terminbeginn; `null` = aus. */
  buchungsschlussStunden: z.number().int().nonnegative().nullable(),
  /** Stornoschluss für Teilnehmer in Stunden vor Terminbeginn; `null` = aus. */
  stornoschlussStunden: z.number().int().nonnegative().nullable(),
  /** Text für Mitarbeiter, die nicht auf der Teilnehmerliste stehen (F-06). */
  ansprechpartner: z.string(),
  /** Trainer-Export (F-26) – bleibt aus bis zur Freigabe durch den CIO (S-08). */
  trainerExportAktiv: z.boolean(),
});
export type Einstellungen = z.infer<typeof EinstellungenSchema>;

/** Der angemeldete Benutzer, wie ihn das Backend aus dem Token ermittelt. */
export const BenutzerKontextSchema = z.object({
  kennung: z.string().min(1),
  name: z.string().min(1),
  rollen: z.array(RolleSchema),
});
export type BenutzerKontext = z.infer<typeof BenutzerKontextSchema>;
